import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  CircularProgress,
  IconButton,
  Popover,
  Theme,
  Typography,
  createStyles,
  makeStyles
} from "@material-ui/core";
import NotificationsIcon from "@material-ui/icons/Notifications";
import BuildIcon from "@material-ui/icons/Build";
import DotIcon from "@material-ui/icons/FiberManualRecord";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime as d } from "luxon";
import { IAppState } from "../../../store";
import { useMutation, useQuery } from "../../../utils/axios-hooks";
import { socket } from "../../../utils/socket";
import { useConsumerScheduledServices } from "../../views/CustomerPortal/ServiceRequests/useConsumerScheduledServices";
import api from "./api";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    icon: {
      color: "inherit"
    },
    popover: {
      "& .MuiPopover-paper": {
        width: 380,
        maxWidth: "90vw",
        maxHeight: 480,
        padding: theme.spacing(2)
      }
    },
    item: {
      display: "flex",
      alignItems: "flex-start",
      padding: theme.spacing(1, 0),
      borderBottom: "1px solid #EEEEEE"
    },
    dot: {
      marginTop: 4,
      marginRight: 8
    },
    time: {
      fontWeight: "normal",
      opacity: 0.6,
      marginLeft: 8
    },
    serviceIcon: {
      marginTop: 2,
      marginRight: 8,
      fontSize: 16,
      color: "#6C5CE7"
    }
  })
);

enum ReadStatus {
  READ = "READ",
  UNREAD = "UNREAD",
  PENDING = "PENDING"
}

interface INotification {
  id: string;
  title: string;
  message: string;
  timeStamp: string;
  status: ReadStatus;
}

// A scheduled-service reminder, normalized to the same shape as INotification
// so both can render in one merged, chronologically sorted list.
interface IFeedItem {
  id: string;
  title: string;
  message: string;
  timeStamp: string;
  status?: ReadStatus;
  kind: "notification" | "scheduledService";
  serviceId?: string;
}

const getTimeElapsed = (timeStamp: string) => {
  const diffMinutes = d.fromISO(timeStamp).diffNow("minutes").minutes;
  const isFuture = diffMinutes > 0;
  const minutes = Math.floor(Math.abs(diffMinutes));
  const days = Math.floor(Math.abs(d.fromISO(timeStamp).diffNow("days").days));
  const label = minutes === 0 ? "now" : minutes < 60 ? `${minutes}m` : minutes < 1440 ? `${Math.floor(minutes / 60)}h` : `${days}d`;
  return isFuture && label !== "now" ? `in ${label}` : label;
};

// Customer-facing bell icon — mirrors nuvven-b2b-app's staff Header/Notification
// component (same notification-api endpoints/WebSocket events), adapted for a
// logged-in customer's own auth identity instead of a staff user's. This is
// what makes Retention Offer "App Notification" deliveries actually visible
// on the website — previously nothing here consumed them at all.
export const NotificationButton: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const userId = useSelector((state: IAppState) => state.authReducer.user?.id);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [badgeCount, setBadgeCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { services: scheduledServices, count: scheduledServicesCount, refetch: refetchScheduledServices } = useConsumerScheduledServices();

  useEffect(() => {
    if (!userId) {
      return;
    }
    socket.auth = { userId };
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, [userId]);

  useMemo(() => {
    socket.on("notification", (...args: any[]) => {
      setNotifications((prev) => [args[0], ...prev]);
    });
    socket.on("count", () => {
      setBadgeCount((prev) => prev + 1);
    });
  }, []);

  const { query: getNotifications, loading } = useQuery<INotification[], void>(api.getNotifications, {
    onSuccess: (data) => {
      setBadgeCount(0);
      setNotifications(data);
    }
  });
  const { query: getCount } = useQuery<{ count: number }, string>(api.getCount, {
    onSuccess: (data) => setBadgeCount(data.count)
  });
  const { mutate: updateReadStatus } = useMutation<INotification, { notificationId: string; status: ReadStatus }>(
    api.updateReadStatus,
    {
      onSuccess: (data) => {
        setNotifications((prev) => prev.map((item) => (item.id === data.id ? data : item)));
      }
    }
  );

  useEffect(() => {
    if (!userId) {
      return;
    }
    if (anchorEl) {
      getNotifications();
      refetchScheduledServices();
    } else {
      getCount(ReadStatus.PENDING);
    }
  }, [anchorEl, userId]);

  if (!userId) {
    return null;
  }

  const open = Boolean(anchorEl);
  const unreadCount = notifications.filter((item) => item.status === ReadStatus.UNREAD).length;

  // Merges the notification-api inbox with scheduled-service reminders
  // (a separate GraphQL-backed feed, not read/unread) into one chronological
  // list so the bell is the single place customers check for both.
  const feedItems: IFeedItem[] = [
    ...notifications.map((n) => ({ ...n, kind: "notification" as const })),
    ...scheduledServices.map((row: any) => ({
      id: `svc-${row.id}`,
      serviceId: row.id,
      title: t("root:scheduledServicesTitle"),
      message: `${row.licencePlate || row.vehicleId} · ${row.serviceCategory || "—"}`,
      timeStamp: row.appointmentDate || row.startDate || new Date().toISOString(),
      kind: "scheduledService" as const
    }))
  ].sort((a, b) => d.fromISO(b.timeStamp).toMillis() - d.fromISO(a.timeStamp).toMillis());

  const combinedBadgeCount = badgeCount + scheduledServicesCount;

  return (
    <>
      <IconButton
        edge="end"
        aria-label="notifications"
        aria-haspopup="true"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Badge badgeContent={combinedBadgeCount} color="error">
          <NotificationsIcon className={classes.icon} fontSize="small" />
        </Badge>
      </IconButton>
      <Popover
        className={classes.popover}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography variant="h6">Notifications</Typography>
        {!loading && (
          <Typography variant="body2" style={{ marginBottom: 12, opacity: 0.7 }}>
            {unreadCount ? `${unreadCount} unread` : "You're all caught up"}
          </Typography>
        )}
        {loading ? (
          <div style={{ textAlign: "center", padding: 16 }}>
            <CircularProgress size={20} />
          </div>
        ) : feedItems.length ? (
          feedItems.map((item) =>
            item.kind === "scheduledService" ? (
              <div
                key={item.id}
                className={classes.item}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  history.push(`/account/scheduled-service/${item.serviceId}`);
                }}
              >
                <BuildIcon className={classes.serviceIcon} />
                <div>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {item.title}
                    <span className={classes.time}>{getTimeElapsed(item.timeStamp)}</span>
                  </Typography>
                  <Typography variant="body2">{item.message}</Typography>
                </div>
              </div>
            ) : (
              <div key={item.id} className={classes.item}>
                <IconButton
                  size="small"
                  className={classes.dot}
                  onClick={() =>
                    updateReadStatus({
                      notificationId: item.id,
                      status: item.status === ReadStatus.READ ? ReadStatus.UNREAD : ReadStatus.READ
                    })
                  }
                  style={{ color: item.status === ReadStatus.READ ? "#BDBDBD" : "#6C5CE7" }}
                >
                  <DotIcon style={{ fontSize: 10 }} />
                </IconButton>
                <div>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {item.title}
                    <span className={classes.time}>{getTimeElapsed(item.timeStamp)}</span>
                  </Typography>
                  <Typography variant="body2">{item.message}</Typography>
                </div>
              </div>
            )
          )
        ) : (
          <Typography variant="body2" style={{ opacity: 0.7, padding: "12px 0" }}>
            No notifications yet.
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default NotificationButton;
